import type { NotificationDto } from '@dbrain/shared';
import { Button, EmptyState, Icons } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { api } from '../api/client.js';
import { keys, useMe, useNotifications } from '../api/hooks.js';
import { isSuperAdmin } from '../api/license.js';

const KIND_ICONS = { approval: Icons.warning, question: Icons.question, task: Icons.done, run_failed: Icons.error, run_interrupted: Icons.error, license: Icons.time } as const;

/**
 * The notification centre (SA-033): cards waiting for the user, finished
 * background work (merged per conversation) and answers that failed or were
 * interrupted. A decided card's notification is read on its own. The
 * license's notices (LisansBildirim, D-127) open the Lisans page for super
 * admins.
 */
export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const admin = isSuperAdmin(useMe().data);
  const read = useMutation({
    mutationFn: (body: { all: true } | { ids: string[] }) => api('POST', '/api/notifications/read', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.notifications }),
  });
  const unread = notifications.data?.unread ?? 0;
  const items = notifications.data?.items ?? [];
  const open = (item: NotificationDto) => {
    if (!item.read) read.mutate({ ids: [item.id] });
    if (item.conversationId) void navigate(`/c/${item.conversationId}`);
    else if (item.kind === 'license' && admin) void navigate('/admin/license');
  };
  const label = unread > 0 ? t('notifications.titleUnread', { count: unread }) : t('notifications.title');
  return (
    <details className="menu bell" data-testid="notifications">
      <summary aria-label={label} title={label}>
        <Icons.bell size={20} />
        {unread > 0 ? (
          <span className="bell__count" data-testid="notifications-unread" aria-hidden="true">
            {unread}
          </span>
        ) : null}
      </summary>
      <div className="menu__panel bell__panel">
        <div className="bell__head">
          <strong>{t('notifications.title')}</strong>
          {unread > 0 ? (
            <Button size="sm" variant="ghost" busy={read.isPending} onClick={() => read.mutate({ all: true })} data-testid="notifications-read-all">
              {t('notifications.readAll')}
            </Button>
          ) : null}
        </div>
        {items.length === 0 ? <EmptyState icon={<Icons.bell />} title={t('notifications.empty')} /> : null}
        <ul className="bell__list">
          {items.map((item) => {
            const Icon = KIND_ICONS[item.kind] ?? Icons.info;
            return (
              <li key={item.id} className={item.read ? 'bell__item' : 'bell__item bell__item--unread'} data-testid="notification-item" data-kind={item.kind}>
                <button type="button" className="ui-menu-item-like" onClick={() => open(item)}>
                  <Icon className="bell__icon" />
                  <span className="bell__kind">{t(`notifications.kinds.${item.kind}`)}</span>
                  <span className="bell__title">
                    {item.title}
                    {item.count > 1 ? ` (${item.count})` : ''}
                  </span>
                  {item.body ? <span className="muted bell__body">{item.body}</span> : null}
                  <span className="muted bell__time">{new Date(item.updatedAt).toLocaleString(i18n.language)}</span>
                  {item.kind === 'license' && admin ? (
                    <span className="ui-button ui-button--primary ui-button--sm bell__action" data-testid="notification-open-license">
                      {t('license.openPage')}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </details>
  );
}
